import React, { useState, useMemo, useRef, useEffect } from 'react';
import { LayoutChangeEvent, Pressable, Text, View, useWindowDimensions } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import RenderHtml from 'react-native-render-html';
import svgIcons from '../../assets';
import { HelpdeskIssueCard, Icon, ModalInfo, Tooltip } from '../index';
import { HelpdeskIssueSearchResult } from '../../client/interfaces';
import { useResize } from '../../utils/resize';
import { isMobile, isNative } from '../../utils/responsive';
import { cln } from '../../utils/classnames';
import { handleLinkPress } from '../../utils';
import { highlightHtml } from '../../utils/highlightHtml';

const MemoizedRenderHtml = React.memo(RenderHtml);

const renderersProps = {
  a: { onPress: (_event: unknown, href: string) => handleLinkPress(href) },
};

const NAV_HEIGHT = 56;

const HelpdeskIssueSection: React.FC<{
  item: HelpdeskIssueSearchResult;
  searchText?: string;
  hideHeader?: boolean;
  showAll?: boolean;
  onToggleShowAll?: () => void;
}> = ({ item, searchText, hideHeader = false, showAll: showAllProp, onToggleShowAll }) => {
  const { t } = useTranslation();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [modalInfoVisible, setModalInfoVisible] = useState(false);
  const tooltipHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTooltip = () => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    setTooltipVisible(true);
  };
  const hideTooltip = () => {
    tooltipHideTimer.current = setTimeout(() => setTooltipVisible(false), 100);
  };
  const { colorScheme } = useColorScheme();
  const { width } = useWindowDimensions();
  const tooltipBaseStyle = useMemo(
    () => ({
      color: colorScheme === 'dark' ? '#101010' : '#f6f6f6',
      fontSize: 12,
      fontFamily: 'sans-400',
      lineHeight: 18,
    }),
    [colorScheme],
  );
  const tooltipTagsStyles = useMemo(
    () => ({
      a: {
        color: colorScheme === 'dark' ? '#623BFF' : '#EDF115',
        textDecorationLine: 'none' as const,
      },
      mark: {
        backgroundColor: colorScheme === 'dark' ? '#EDF115' : '#8509ff',
        color: colorScheme === 'dark' ? '#101010' : '#FAFAFA',
      },
    }),
    [colorScheme],
  );
  const [showAllInternal, setShowAllInternal] = useState(false);
  const showAll = showAllProp !== undefined ? showAllProp : showAllInternal;
  const toggleShowAll = onToggleShowAll ?? (() => setShowAllInternal((v) => !v));
  const { isWidthBelowSmall, isWidthBelowLarge, isWidthBelowXLarge } = useResize();

  const gap = 12;

  const numColumns = isWidthBelowSmall ? 1 : isWidthBelowLarge ? 2 : isWidthBelowXLarge ? 3 : 4;
  const cardWidth = `calc(${100 / numColumns}% - ${(gap * (numColumns - 1)) / numColumns}px)`;
  // Collapsed state shows one row's worth of cards: narrow layouts (native and
  // single-column web) show 2, wider web shows exactly numColumns (one row).
  const collapsedCount = isNative || isWidthBelowSmall ? 2 : numColumns;
  const hasMore = item.helpdeskIssues.length > collapsedCount;

  const visibleIssues = showAll
    ? item.helpdeskIssues
    : item.helpdeskIssues.slice(0, collapsedCount);

  // Web reveal animation: the section height animates between the (measured)
  // first-row height and the (measured) full height. Cards are revealed by the
  // growing height rather than animating individually.
  const firstRowIssues = item.helpdeskIssues.slice(0, collapsedCount);
  const restIssues = item.helpdeskIssues.slice(collapsedCount);
  const collapsedH = useSharedValue(0);
  const fullH = useSharedValue(0);
  const collapsedMeasured = useSharedValue(false);
  const progress = useSharedValue(showAll ? 1 : 0);
  // Rest cards mount only after the first expand (kept mounted afterwards,
  // clipped when collapsed) so the initial collapsed render never flashes them.
  const [restMounted, setRestMounted] = useState(showAll);

  useEffect(() => {
    if (showAll) setRestMounted(true);
    progress.value = withTiming(showAll ? 1 : 0, {
      duration: 250,
      easing: Easing.out(Easing.cubic),
    });
  }, [showAll, progress]);

  const onFirstRowLayout = (e: LayoutChangeEvent) => {
    collapsedH.value = e.nativeEvent.layout.height;
    collapsedMeasured.value = collapsedH.value > 0;
  };
  const onContentLayout = (e: LayoutChangeEvent) => {
    fullH.value = e.nativeEvent.layout.height;
  };

  const animatedContainerStyle = useAnimatedStyle(() => {
    if (!hasMore || !collapsedMeasured.value) return {};
    return { height: collapsedH.value + (fullH.value - collapsedH.value) * progress.value };
  }, [hasMore]);

  return (
    <View
      className={cln(
        'border-neutral-200 dark:border-neutral-910',
        isMobile ? 'border-b-[1px] pl-[30px] pr-[30px] pb-6' : 'border pb-6 pr-6 pl-6 h-auto mb-6',
      )}
      testID="helpdesk-issue-section"
    >
      {/*
       * Cover sticks at top:0 and fills the navbar zone (0–56px) with the page
       * background, preventing cards from showing through the semi-transparent navbar.
       * marginBottom: -NAV_HEIGHT collapses its space so normal flow is unaffected.
       */}
      {!isMobile ? (
        <View
          className="bg-neutral-50 dark:bg-neutral-950"
          // @ts-expect-error position sticky is valid on web
          style={{
            position: 'sticky',
            top: 0,
            height: NAV_HEIGHT,
            marginBottom: -NAV_HEIGHT,
            zIndex: 9,
          }}
        />
      ) : (
        <></>
      )}

      {/* Topic header – desktop only; native uses SectionList renderSectionHeader */}
      {!hideHeader && (
        <View
          className="flex-row items-center bg-neutral-50 dark:bg-neutral-950 pb-3 pt-6"
          // @ts-expect-error position sticky is valid on web
          style={{
            position: showAll ? 'sticky' : 'relative',
            top: showAll && !isMobile ? NAV_HEIGHT : 0,
            zIndex: 10,
          }}
        >
          <Text className="text-body sm:text-h3 text-neutral-950 dark:text-neutral-50 font-[sans-700] mr-3">
            {item.topicName}
          </Text>
          {item.topicDescription && item.helpdeskIssues.length > 0 ? (
            <Pressable
              testID="helpdesk-topic-info"
              style={{ position: 'relative' }}
              onHoverIn={() => !isMobile && showTooltip()}
              onHoverOut={() => !isMobile && hideTooltip()}
              onPress={() => isMobile && setModalInfoVisible(true)}
            >
              <Icon
                icon={
                  searchText &&
                  item.topicDescription.toLowerCase().includes(searchText.toLowerCase())
                    ? svgIcons.infoIconPrimary
                    : svgIcons.infoIcon
                }
                mobileSize={{ width: 24, height: 24 }}
                webSize={{ width: 32, height: 32 }}
              />
              {tooltipVisible && (
                <Tooltip testID="helpdesk-topic-tooltip">
                  <MemoizedRenderHtml
                    contentWidth={width}
                    source={{ html: highlightHtml(item.topicDescription, searchText) }}
                    baseStyle={tooltipBaseStyle}
                    renderersProps={renderersProps}
                    tagsStyles={tooltipTagsStyles}
                  />
                </Tooltip>
              )}
              <ModalInfo
                testID="helpdesk-issue-section-modal"
                showModal={modalInfoVisible}
                closeModal={() => setModalInfoVisible(false)}
                text={item.topicDescription}
                search={searchText}
              />
            </Pressable>
          ) : null}
        </View>
      )}

      {/* Cards grid or fallback description */}
      {item.helpdeskIssues.length > 0 ? (
        <View className="z-0">
          {isNative ? (
            <View className="flex-row flex-wrap" style={{ columnGap: gap, rowGap: gap }}>
              {visibleIssues.map((issue) => (
                <HelpdeskIssueCard
                  key={issue.helpdeskIssueId}
                  item={issue}
                  searchText={searchText}
                />
              ))}
            </View>
          ) : (
            <Animated.View style={[{ overflow: 'hidden' }, animatedContainerStyle]}>
              <View onLayout={onContentLayout}>
                <View
                  className="flex-row flex-wrap"
                  style={{ columnGap: gap, rowGap: gap }}
                  onLayout={onFirstRowLayout}
                >
                  {firstRowIssues.map((issue) => (
                    <HelpdeskIssueCard
                      key={issue.helpdeskIssueId}
                      item={issue}
                      cardWidth={cardWidth}
                      searchText={searchText}
                    />
                  ))}
                </View>
                {restMounted && restIssues.length > 0 ? (
                  <View
                    className="flex-row flex-wrap"
                    style={{ columnGap: gap, rowGap: gap, marginTop: gap }}
                  >
                    {restIssues.map((issue) => (
                      <HelpdeskIssueCard
                        key={issue.helpdeskIssueId}
                        item={issue}
                        cardWidth={cardWidth}
                        searchText={searchText}
                      />
                    ))}
                  </View>
                ) : null}
              </View>
            </Animated.View>
          )}
          {hasMore ? (
            <Pressable
              testID="helpdesk-show-more"
              onPress={toggleShowAll}
              className="flex-row items-center justify-center mt-3"
              style={{ gap: 8 }}
            >
              <Text className="text-body text-neutral-950 dark:text-neutral-50 font-[sans-400]">
                {showAll ? t('helpdesk:show_less') : t('helpdesk:show_more')}
              </Text>
              <Icon
                icon={showAll ? svgIcons.chevronUpIcon : svgIcons.chevronDownIcon}
                mobileSize={{ width: 20, height: 20 }}
                webSize={{ width: 20, height: 20 }}
              />
            </Pressable>
          ) : null}
        </View>
      ) : (
        <Text className="text-body text-neutral-950 dark:text-neutral-50 font-[sans-400]">
          {item.topicDescription}
        </Text>
      )}
    </View>
  );
};

export default HelpdeskIssueSection;
